'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { mockHomeContentList } from '@/lib/data/mockHomeData'
import { useHLClearingHouseState } from '@/lib/hooks/useMargin'
import { useUserFills } from '@/lib/hooks/useUserFills'
import type { UserFills } from '@/lib/utils/hyper/types'
import { useParams, useRouter, useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { HexString } from '@/lib/config/wagmi'
import { addSymbolSuffix } from '@/lib/hooks/useHLCoinInfo'
import { PositionHistoryItem } from '@/components/trader/PositionHistoryItem'
import { Box, Grid, HStack, Stack, Text, VStack } from '@chakra-ui/react'
import { PositionDetailHeader } from '@/components/trader/PositionDetailHeader'
import { LoadingState } from '@/components/common/LoadingState'
import { PositionDetailNav } from '@/components/trader/PositionDetailNav'
import { useVirtualizer } from '@tanstack/react-virtual'
import { formatPercentageDisplay } from '@/lib/utils/format'
import { useMarketTicker } from '@/lib/hooks/usePerpMarket'
import { TraderDetailHeader } from '@/components/trader/TraderDetailHeader'
import { TagsSection } from '@/components/trader/TagsSection'
import { StorySection } from '@/components/trader/StorySection'
import { useTraderProfile } from '@/lib/hooks/useTraderProfile'
import { renderTagColor } from '@/lib/utils/tagColor.utils'
import { DownloadMobileAppCard } from '@/components/trader/DownloadMobileAppCard'
import CommonContainer from '@/components/common/CommonContainer'
import { ChartInterval, PositionDetailChart } from '@/components/trader/PositionDetailChart'
import { PositionDetailCard } from '@/components/trader/PositionDetailCard'

export default function PositionDetailPagePC() {
  const t = useTranslations()

  const router = useRouter()

  const params = useParams()
  const searchParams = useSearchParams()
  const address = params.address as string
  const positionId = params.positionId as string
  const from = searchParams.get('from')
  const nonce = searchParams.get('nonce')

  const { data: traderProfile } = useTraderProfile(address);

  // Track if user actually navigated from the trader page via app navigation
  const [canUseBack, setCanUseBack] = useState(false)

  // 图表间隔状态管理
  const [chartInterval, setChartInterval] = useState<ChartInterval>('1h');

  useEffect(() => {
    if (typeof window !== 'undefined' && address) {
      // Check sessionStorage for navigation marker set by the previous page
      const navigationKey = `nav_from_trader_${address}`
      const hasNavigationMarker = !!nonce && sessionStorage.getItem(navigationKey) === nonce

      setCanUseBack(hasNavigationMarker)
    }
  }, [address])

  const listRef = useRef<HTMLDivElement>(null)

  const { data: clearingHouseState, isLoading } = useHLClearingHouseState({
    user: address as HexString,
  })

  const coinSymbol = positionId?.replaceAll('-PERP', '') || ''
  // Fetch user fills for history
  const { data: userFills, isLoading: isHistoryLoading } = useUserFills(address as HexString)

  const { data: marketTicker } = useMarketTicker(coinSymbol, undefined)

  const filteredFills = userFills?.filter(
    fill => fill.coin.replaceAll('-PERP', '') === positionId.replaceAll('-PERP', ''),
  )

  const fillVirtualizer = useVirtualizer({
    count: filteredFills?.length || 0,
    getScrollElement: () => listRef.current,
    estimateSize: () => 50 + 6 * 4,
    overscan: 5,
  })

  // Find the specific position
  const position = clearingHouseState?.assetPositions.find(
    ap => ap.position.coin === addSymbolSuffix(positionId),
  )?.position || null

  // Find user data from mock data
  const userData = mockHomeContentList.find(trader => trader.user.address === address)?.user || null

  // Calculate price display data
  let priceDisplayData: { price: number, changePercent: string, isPositive: boolean }

  if (!position) {
    priceDisplayData = { price: 0, changePercent: '- -', isPositive: true }
  } else {
    const entryPrice = Number.parseFloat(position.entryPx || '0')
    const unrealizedPnl = Number.parseFloat(position.unrealizedPnl || '0')
    const roi = Number.parseFloat(position.returnOnEquity || '0')

    // Calculate current price based on entry price and PnL
    const currentPrice = marketTicker?.mark_price ?? marketTicker?.last_price ?? 0
    const changePercent = marketTicker?.change_24h_percent ? formatPercentageDisplay(marketTicker?.change_24h_percent) : '---'
    const isPositive = marketTicker?.change_24h_percent ? marketTicker.change_24h_percent >= 0 : false

    priceDisplayData = {
      price: currentPrice,
      changePercent,
      isPositive: isPositive,
    }
  }

  // console.log('Position Detail Page', { address, positionId, position }, clearingHouseState?.assetPositions)
  const handleGoBack = useCallback(() => {
    // Only use router.back() if user actually came from the trader page
    // This prevents issues when users directly access the URL (even with ?from=trader)
    if (from === 'trader' && canUseBack) {
      router.back()
    }
    else {
      // Always navigate to the trader page as a fallback
      // This handles:
      // 1. Direct URL access (with or without ?from=trader)
      // 2. Navigation from other sources
      // 3. Bookmarks/shared links
      router.push(`/trader/${address}`)
    }
  }, [from, canUseBack, router, address])
  const handleAvatarPress = () => {
    handleGoBack()
  }

  if (isLoading) {
    return (
      <Box flex={1}>
        <LoadingState />
      </Box>
    )
  }

  if (!position) {
    return (
      <Box flex={1}>
        <HStack flex={1} align="center" justify="center" px={4}>
          <Text textAlign="center" fontSize="md" color="text.t1">{t('trader.position.notFound')}</Text>
        </HStack>
      </Box>
    )
  }

  return (
    <CommonContainer borderTop="1px solid rgba(255, 255, 255, 0.10)" bg="#000" overflow="auto">
      <Grid pt={4} mb={6} gap={10} templateColumns={{ base: '1fr', md: '7fr 3fr' }}>
        <VStack flex={1} gap={4} align="flex-start">
          <TraderDetailHeader
            traderName={traderProfile?.nickname ?? '- -'}
            traderAddress={address}
          />

          {/* Tags Section */}
          <TagsSection
            tags={renderTagColor(traderProfile?.labels ?? [])}
          />

          {/* Story Section */}
          <StorySection
            data={{
              description: traderProfile?.trader ?? '- -',
              address,
              nickname: traderProfile?.nickname ?? '- -',
            }}
          />
        </VStack>

        <DownloadMobileAppCard traderName={traderProfile?.nickname ?? '- -'} />
      </Grid>

      <Stack direction={{ base: 'column', md: 'row' }} gap={6} pb={10} align="flex-start">
        <VStack w={{ base: '100%', md: 'auto' }} flex={1} gap={6}>
          {/* Position Detail Card */}
          <Box w="full" py={6} px={2} border=" 1px solid rgba(255, 255, 255, 0.15)" rounded="16px">
            <PositionDetailCard position={position} />
          </Box>

          {/* Position Detail Chart */}
          <Box w="full" p={6} border=" 1px solid rgba(255, 255, 255, 0.15)" rounded="16px">
            <PositionDetailChart
              position={position}
              filteredFills={filteredFills}
              coinSymbol={position?.coin}
              interval={chartInterval}
              onIntervalChange={setChartInterval}
            />
          </Box>
        </VStack>

        <VStack w={{ base: '100%', md: 'auto' }} flex={1} p={6} border="1px solid rgba(255, 255, 255, 0.15)" rounded="16px" minH="full" overflow="hidden">
          <Text mb={6} w="full" fontSize="20px" color="text.t1" fontWeight="700">
            History
          </Text>

          {filteredFills && filteredFills.length > 0 ? (
            <Box
              w="full"
              overflowY="hidden"
              h={518}
            >
              <Box h="full" ref={listRef} overflowY="auto">
                <Box pos="relative" height={`${fillVirtualizer.getTotalSize()}px`}>
                {fillVirtualizer.getVirtualItems().map((item) => (
                  <Box
                    key={filteredFills[item.index].tid}
                    pos="absolute"
                    top={0}
                    left={0}
                    h={`${item.size}px`}
                    w="full"
                    transform={`translateY(${item.start}px)`}
                  >
                    <PositionHistoryItem fill={filteredFills[item.index]} index={item.index} totalItems={filteredFills?.length || 0} />
                  </Box>
                ))}
              </Box>
              </Box>
            </Box>
          ) : (
            <HStack flex={1} align="center" justify="center" py={8}>
              {/* Empty State */}
              <Text textAlign="center" fontSize="sm" color="text.t1/60">
                {isHistoryLoading ? t('trader.history.loading') : t('trader.history.noHistory')}
              </Text>
            </HStack>
          )}
        </VStack>
      </Stack>
    </CommonContainer>
  )
}
